import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import CategoryGrid from "./components/CategoryGrid";
import AboutSection from "./components/AboutSection";
import ArticleGrid from "./components/ArticleGrid";
import TestimonialSection from "./components/TestimonialSection";
import CTASection from "./components/CTASection";
import { mainSiteUrl } from "@/mocks/categories";
import { useScrollReveal } from "@/hooks/useScrollReveal";

function SectionWrapper({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
    >
      {children}
    </div>
  );
}

export default function Home() {
  useEffect(() => {
    const form = document.getElementById("newsletter-form") as HTMLFormElement | null;
    if (!form) return;

    const handleSubmit = async (e: Event) => {
      e.preventDefault();
      const target = e.target as HTMLFormElement;
      const messageEl = document.getElementById("newsletter-message");
      if (!messageEl) return;

      try {
        const formData = new FormData(target);
        const response = await fetch(target.action, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        });
        if (response.ok) {
          messageEl.textContent = "Thanks for subscribing! Check your inbox soon.";
          messageEl.classList.remove("hidden");
          messageEl.classList.add("text-accent-400");
          target.reset();
        } else {
          throw new Error("Submission failed");
        }
      } catch {
        messageEl.textContent = "Something went wrong. Please try again.";
        messageEl.classList.remove("hidden");
        messageEl.classList.add("text-red-400");
      }
    };

    form.addEventListener("submit", handleSubmit);
    return () => form.removeEventListener("submit", handleSubmit);
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection />

        <SectionWrapper>
          <ComparisonSection />
        </SectionWrapper>

        <SectionWrapper>
          <CategoryGrid />
        </SectionWrapper>

        <SectionWrapper>
          <AboutSection />
        </SectionWrapper>

        <SectionWrapper>
          <ArticleGrid />
        </SectionWrapper>

        <SectionWrapper>
          <TestimonialSection />
        </SectionWrapper>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}

function ComparisonSection() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              Mulebuy Spreadsheet vs <span className="text-primary-500">Alternatives</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              See how the mulebuy spreadsheet system compares against traditional agent platforms and other shopping methods.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="border-b-2 border-background-200">
                  <th className="text-left py-4 px-4 font-heading text-base font-semibold text-foreground-950">Platform</th>
                  <th className="text-left py-4 px-4 font-heading text-base font-semibold text-foreground-950">Speed</th>
                  <th className="text-left py-4 px-4 font-heading text-base font-semibold text-foreground-950">Transparency</th>
                  <th className="text-left py-4 px-4 font-heading text-base font-semibold text-foreground-950">Price</th>
                  <th className="text-left py-4 px-4 font-heading text-base font-semibold text-foreground-950">Safety</th>
                  <th className="text-left py-4 px-4 font-heading text-base font-semibold text-foreground-950">Ease of Use</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200 bg-primary-50/30">
                  <td className="py-4 px-4">
                    <span className="font-semibold text-foreground-950 text-sm">Mulebuy Spreadsheet</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">High</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">High</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">Low</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full">Med-High</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">Easy</span>
                  </td>
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-4 px-4">
                    <span className="font-medium text-foreground-600 text-sm">Traditional Agent</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Low</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-4 px-4">
                    <span className="font-medium text-foreground-600 text-sm">Alt Spreadsheet Tools</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Varies</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full">Medium</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/mulebuy-spreadsheet-vs-alternatives"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              Read Full Comparison
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}